import { useEffect, useState } from "react";
import {
  RouterProvider,
  createRouter,
  createRoute,
  createRootRoute,
  Outlet,
  useLocation,
} from "@tanstack/react-router";
import { useSelector } from "react-redux";
import ManageCourses from "../src/pages/manageCourses";
import { Toaster } from "../@/components/ui/sonner";
import CourseManagementPage from "./pages/courseDetailPage";
import { Layout } from "./components/layout";
import { AuthenticationPage } from "./pages/authenticationPage";
// import LiveClassesPage from "./pages/manageLiveClasses";
import ViewRecordings from "./pages/viewRecordings";
import TrackStudents from "./pages/trackMyStudents";
import AdminPortal from "./pages/adminPortal";
import ReviewProjects from "./pages/reviewProjects";
import NotificationsPage from "./pages/notificationsPage";
import ManagePathways from "./pages/managePathways";
import ManageCohorts from "./pages/manageCohorts";
import LiveClassesPage from "./pages/LiveClassesPage";
import GlobalTaxonomies from "./pages/globalTaxonomies";
import ResponsiveLayout from "./shared/responsiveLayout";
// import LessonExplorerBadges from "./components/badgeCard";
import ProtectedRoute from "./components/ProtectedRoute";
import RolesBootstrapper from "./auth/RolesBootstrapper";
import PublicProfile from "./components/badgeCard";
import DashboardPage from "./pages/dashboard";
import ManageBilling from "./pages/manageBilling";
import ComingSoon from "./shared/comingSoon";

function AdminDashboardGate() {
  const roles = useSelector((s) => s.auth.roles) || [];
  const isAdmin = roles.includes("Admin");
  const [adminModeOn, setAdminModeOn] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem("zindua_admin_mode") === "on";
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    const sync = () => {
      setAdminModeOn(window.localStorage.getItem("zindua_admin_mode") === "on");
    };
    window.addEventListener("zindua-mode-changed", sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener("zindua-mode-changed", sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  if (isAdmin && adminModeOn) {
    return (
      <ComingSoon
        title="Admin Dashboard"
        description="In this page, you'll be able to track overall analytics for the entire organisations. You'll get a summary of performance across cohorts, courses, and pathways as well as AI reports on quality assurance and drop-out risk assessments."
      />
    );
  }

  return <DashboardPage />;
}

function RootRouteError({ error, reset }) {
  const message =
    error instanceof Error
      ? error.message
      : typeof error === "string"
        ? error
        : "An unexpected error occurred";

  const stack = error instanceof Error ? error.stack : null;

  return (
    <div style={{ padding: 16 }}>
      <h1 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>
        Something went wrong
      </h1>
      <div style={{ marginBottom: 12, color: "#444" }}>{message}</div>
      {stack ? (
        <pre
          style={{
            whiteSpace: "pre-wrap",
            background: "#f6f6f6",
            padding: 12,
            borderRadius: 8,
            border: "1px solid #e6e6e6",
            maxHeight: 240,
            overflow: "auto",
          }}
        >
          {stack}
        </pre>
      ) : null}
      <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
        <button
          type="button"
          onClick={() => {
            if (typeof reset === "function") reset();
            else window.location.reload();
          }}
          style={{
            padding: "8px 12px",
            borderRadius: 8,
            border: "1px solid #ddd",
            background: "white",
            cursor: "pointer",
          }}
        >
          Try again
        </button>
        <button
          type="button"
          onClick={() => window.location.reload()}
          style={{
            padding: "8px 12px",
            borderRadius: 8,
            border: "1px solid #ddd",
            background: "white",
            cursor: "pointer",
          }}
        >
          Reload
        </button>
      </div>
    </div>
  );
}

function toTitleCaseFromPathSegment(segment) {
  return String(segment || "")
    .replace(/[-_]+/g, " ")
    .replace(/\b\w/g, (m) => m.toUpperCase())
    .trim();
}

function getSeoTitleForPath(pathname) {
  // Homepage
  if (pathname === "/") return "Zindua LMS Admin Portal";

  // Known app routes
  const staticTitleMap = {
    "/manage-courses": "Manage Courses",
    "/live-classes": "Live Classes",
    "/view-recordings": "View Recordings",
    "/track-students": "Track My Students",
    "/review-projects": "Review Projects",
    "/lesson-badges": "Lesson Badges",
    "/admin-portal": "Admin Portal",
    "/manage-pathways": "Manage Pathways",
    "/manage-cohorts": "Manage Cohorts",
    "/global-taxonomies": "Global Taxonomies",
    "/manage-billing": "Manage Billing",
    "/billing-customers": "Billing Customers",
    "/billing-payment-plans": "Billing Payment Plans",
    "/overall-analytics": "Overall Analytics",
    "/billing-issues": "Billing Issues",
    "/manage-certificates": "Manage Certificates",
    "/course-analytics": "Course Analytics",
    "/course-extras": "Course Extras",
    "/org-management": "Org Management",
    "/job-board/jobs": "Job Board Jobs",
    "/job-board/companies": "Job Board Companies",
    "/job-board/applications": "Job Board Applications",
    "/sign-in": "Sign In",
    "/sign-up": "Sign Up",
  };

  // Dynamic route(s)
  if (pathname.startsWith("/manage-course/")) {
    return "Course Details – Zindua LMS Admin";
  }

  const fromMap = staticTitleMap[pathname];
  if (fromMap) return `${fromMap} – Zindua LMS Admin`;

  // Fallback: use last path segment
  const lastSegment = pathname.split("/").filter(Boolean).pop();
  const derived = toTitleCaseFromPathSegment(lastSegment);
  return `${derived || "Page"} – Zindua LMS Admin`;
}

function DocumentTitleManager() {
  const location = useLocation();

  useEffect(() => {
    if (typeof document === "undefined") return;
    document.title = getSeoTitleForPath(location?.pathname || "/");
  }, [location?.pathname]);

  return null;
}

// Helper to allow nested routes to work with ProtectedRoute
const LayoutWrapper = () => {
  return (
    <ResponsiveLayout>
      <Outlet />
    </ResponsiveLayout>
  );
};

const rootRoute = createRootRoute({
  component: () => (
    <RolesBootstrapper>
      <DocumentTitleManager />
      <Outlet />
      <Toaster position="top-right" richColors />
    </RolesBootstrapper>
  ),
  errorComponent: RootRouteError,
  notFoundComponent: () => <div style={{ padding: 16 }}>Not Found</div>,
});

const signInRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "sign-in",
  component: AuthenticationPage,
});

// Catch-all for Clerk multi-step sign-in routes (factor-one, forgot-password, etc.)
const signInStepRoute = createRoute({
  getParentRoute: () => signInRoute,
  path: "$clerkStep",
  component: AuthenticationPage,
});

// Clerk SignIn sub-routes (multi-step flow)
const signInFactorOneRoute = createRoute({
  getParentRoute: () => signInRoute,
  path: "factor-one",
  component: AuthenticationPage,
});

const signInForgotPasswordRoute = createRoute({
  getParentRoute: () => signInRoute,
  path: "forgot-password",
  component: AuthenticationPage,
});

const signInResetPasswordRoute = createRoute({
  getParentRoute: () => signInRoute,
  path: "reset-password",
  component: AuthenticationPage,
});

const signUpRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "sign-up",
  component: AuthenticationPage,
});

// Catch-all for Clerk multi-step sign-up routes (verify-email, continue, set-password, etc.)
const signUpStepRoute = createRoute({
  getParentRoute: () => signUpRoute,
  path: "$clerkStep",
  component: AuthenticationPage,
});

// Clerk SignUp sub-routes (multi-step flow)
const signUpVerifyEmailRoute = createRoute({
  getParentRoute: () => signUpRoute,
  path: "verify-email",
  component: AuthenticationPage,
});

const signUpSetPasswordRoute = createRoute({
  getParentRoute: () => signUpRoute,
  path: "set-password",
  component: AuthenticationPage,
});

const protectedLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "protected",
  component: () => (
    <ProtectedRoute>
      <LayoutWrapper />
    </ProtectedRoute>
  ),
});

const dashboardRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: "/",
  component: AdminDashboardGate,
});

const manageCoursesRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: "manage-courses",
  component: ManageCourses,
});

const courseManagementRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: "manage-course/$courseId",
  component: CourseManagementPage,
});

const liveClassesRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: "live-classes",
  component: LiveClassesPage,
});

const viewRecordingsRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: "view-recordings",
  component: ViewRecordings,
});

const trackStudentsRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: "track-students",
  component: TrackStudents,
});


const reviewProjectsRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: "review-projects",
  component: ReviewProjects,
});

const notificationsRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: "notifications",
  component: NotificationsPage,
});

const lessonBadgesRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: "lesson-badges",
  component: PublicProfile,
});

const adminPortalRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: "admin-portal",
  component: () => (
    <ProtectedRoute requiredRoles={["Admin"]}>
      <AdminPortal />
    </ProtectedRoute>
  ),
});

const managePathwaysRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: "manage-pathways",
  component: () => (
    <ProtectedRoute requiredRoles={["Admin", "Manager"]}>
      <ManagePathways />
    </ProtectedRoute>
  ),
});

const manageCohortsRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: "manage-cohorts",
  component: () => (
    <ProtectedRoute requiredRoles={["Admin", "Manager"]}>
      <ManageCohorts />
    </ProtectedRoute>
  ),
});

const globalTaxonomiesRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: "global-taxonomies",
  component: () => (
    <ProtectedRoute requiredRoles={["Admin", "Manager"]}>
      <GlobalTaxonomies />
    </ProtectedRoute>
  ),
});

const manageBillingRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: "manage-billing",
  component: () => (
    <ProtectedRoute requiredRoles={["Admin"]}>
      <ComingSoon
        title="Billing Management"
        description="In this page, you'll be able to track customers and receivables; view invoices and payments; as well as handle billing issues like refunds and penalties."
      />
    </ProtectedRoute>
  ),
});


const billingCustomersRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: "billing-customers",
  component: () => (
    <ProtectedRoute requiredRoles={["Admin", "Manager"]}>
      <ComingSoon
        title="Customers"
        description="View customer profiles, track subscription status, and manage billing-related details in one place."
      />
    </ProtectedRoute>
  ),
});

const billingPaymentPlansRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: "billing-payment-plans",
  component: () => (
    <ProtectedRoute requiredRoles={["Admin", "Manager"]}>
      <ComingSoon
        title="Payment Plans"
        description="Create, update, and organize pricing plans so learners always see the right options."
      />
    </ProtectedRoute>
  ),
});

const overallAnalyticsRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: "overall-analytics",
  component: () => (
    <ProtectedRoute requiredRoles={["Admin", "Manager"]}>
      <ComingSoon
        title="Overall Analytics"
        description="Get a high-level view of performance across courses, cohorts, and activity trends."
      />
    </ProtectedRoute>
  ),
});

const billingIssuesRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: "billing-issues",
  component: () => (
    <ProtectedRoute requiredRoles={["Admin", "Manager"]}>
      <ComingSoon
        title="Billing Issues"
        description="Review failed payments, billing errors, and outstanding issues that need your attention."
      />
    </ProtectedRoute>
  ),
});

const manageCertificatesRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: "manage-certificates",
  component: () => (
    <ProtectedRoute requiredRoles={["Admin", "Manager"]}>
      <ComingSoon
        title="Manage Certificates"
        description="In this page, you'll be to track/manage issued certificates and build certificate templates for courses and pathways."
      />
    </ProtectedRoute>
  ),
});

// Shown to instructors + admin/manager (signed-in users)
const courseAnalyticsRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: "course-analytics",
  component: () => (
    <ComingSoon
      title="Course Analytics"
      description="In this page, you will be able to track student performance for topics, lessons, quizzes, and assignments associated with your courses. You'll also have recommendations for how to improve the course content."
    />
  ),
});

// Shown to instructors + admin/manager (signed-in users)
const courseExtrasRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: "course-extras",
  component: () => (
    <ComingSoon
      title="Course Extras"
      description="In this page, you'll be able to add supporting resources for students to explore (also used train the AI assistant) as well as create challenges to offer practice outside the standard course experience."
    />
  ),
});

const orgManagementRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: "org-management",
  component: () => (
    <ProtectedRoute requiredRoles={["Admin"]}>
      <ComingSoon
        title="Org Management"
        description="Manage organizations, teams, and access controls so the right people have the right tools."
      />
    </ProtectedRoute>
  ),
});

const jobBoardJobsRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: "job-board/jobs",
  component: () => (
    <ProtectedRoute requiredRoles={["Admin", "Manager"]}>
      <ComingSoon
        title="All Jobs/ Opportunities"
        description="Browse and manage job opportunities shared with learners, including visibility and status."
      />
    </ProtectedRoute>
  ),
});

const jobBoardCompaniesRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: "job-board/companies",
  component: () => (
    <ProtectedRoute requiredRoles={["Admin", "Manager"]}>
      <ComingSoon
        title="All Companies"
        description="Maintain company profiles and employer details that power the job board experience."
      />
    </ProtectedRoute>
  ),
});

const jobBoardApplicationsRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: "job-board/applications",
  component: () => (
    <ProtectedRoute requiredRoles={["Admin", "Manager"]}>
      <ComingSoon
        title="Applications"
        description="Review job applications and track candidate progress from submission to decision."
      />
    </ProtectedRoute>
  ),
});


const routeTree = rootRoute.addChildren([
  signInRoute.addChildren([
    signInStepRoute,
    signInFactorOneRoute,
    signInForgotPasswordRoute,
    signInResetPasswordRoute,
  ]),
  signUpRoute.addChildren([signUpStepRoute, signUpVerifyEmailRoute, signUpSetPasswordRoute]),
  protectedLayoutRoute.addChildren([
    dashboardRoute,
    manageCoursesRoute,
    courseManagementRoute,
    liveClassesRoute,
    viewRecordingsRoute,
    trackStudentsRoute,
    reviewProjectsRoute,
    notificationsRoute,
    lessonBadgesRoute,
    adminPortalRoute,
    managePathwaysRoute,
    manageCohortsRoute,
    globalTaxonomiesRoute,
    manageBillingRoute,
    billingCustomersRoute,
    billingPaymentPlansRoute,
    overallAnalyticsRoute,
    billingIssuesRoute,
    manageCertificatesRoute,
    courseAnalyticsRoute,
    courseExtrasRoute,
    orgManagementRoute,
    jobBoardJobsRoute,
    jobBoardCompaniesRoute,
    jobBoardApplicationsRoute,
  ]),
]);

const router = createRouter({ routeTree });

export default function App() {
  return <RouterProvider router={router} />;
}
